import React, { useState, useContext, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, ExternalLink } from 'lucide-react';
import apiClient from '../../api/apiClient';
import { AuthContext } from '../../context/authContext';
import { Button } from './button';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

/**
 * Convert a base64url string to a Uint8Array — needed for VAPID public key.
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}

/**
 * Request notification permission and subscribe the browser to push.
 * Saves the subscription to the backend.
 */
async function subscribeUserToPush() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('[PUSH] Push notifications not supported on this browser.');
        return;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        console.warn('[PUSH] Notification permission denied.');
        return;
    }

    try {
        // Get VAPID public key from backend
        const { data } = await apiClient.get('/notifications/vapid-public-key');
        const vapidPublicKey = data.publicKey;
        if (!vapidPublicKey) {
            console.warn('[PUSH] No VAPID public key from server.');
            return;
        }

        const registration = await navigator.serviceWorker.ready;

        // Check if already subscribed
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
            // Already subscribed — ensure backend knows about this subscription
            await apiClient.post('/notifications/subscribe', {
                subscription: existingSubscription.toJSON()
            });
            return;
        }

        // Subscribe to push
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });

        // Save subscription to backend
        await apiClient.post('/notifications/subscribe', {
            subscription: subscription.toJSON()
        });

        console.log('[PUSH] Subscribed to push notifications.');
    } catch (err) {
        console.error('[PUSH] Failed to subscribe:', err);
    }
}

const NotificationDropdown = ({ align = 'right' }) => {
    const { user } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState(
        typeof window !== 'undefined' && 'Notification' in window
            ? Notification.permission
            : 'default'
    );
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const hasSubscribed = useRef(false);

    const { data: notifications = [] } = useQuery({
        queryKey: ['notifications', user?._id],
        queryFn: async () => {
            const { data } = await apiClient.get('/notifications');
            return data;
        },
        enabled: !!user,
        refetchInterval: 30000, // Still poll in-app for badge count updates
    });

    // Subscribe to push notifications once when user logs in
    useEffect(() => {
        if (!user || hasSubscribed.current) return;
        hasSubscribed.current = true;
        subscribeUserToPush().then(() => {
            // Refresh permission state after attempting subscribe
            if ('Notification' in window) {
                setPermissionStatus(Notification.permission);
            }
        });
    }, [user]);

    const markReadMutation = useMutation({
        mutationFn: async (id) => {
            await apiClient.put(`/notifications/${id}/read`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['notifications']);
        },
    });

    const unreadCount = notifications.filter(n => !n.read).length;

    const handleMarkRead = (id) => {
        markReadMutation.mutate(id);
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            handleMarkRead(notification._id);
        }
        setIsOpen(false);
        const fallbackUrl = user?.role === 'Administrator' ? '/admin-dashboard' : '/donordashboard';
        navigate(notification.redirectUrl || fallbackUrl);
    };

    return (
        <div className="relative">
            <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setIsOpen(!isOpen)}
            >
                <Bell className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-zinc-950" />
                )}
            </Button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-80 max-h-[80vh] overflow-y-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-2xl z-[100] p-2">
                        <div className="px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 mb-2">
                            <h3 className="font-semibold text-sm text-zinc-900 dark:text-white">Notifications</h3>
                        </div>
                        {/* Show warning if push notifications are blocked by browser */}
                        {permissionStatus === 'denied' && (
                            <div className="mx-2 mb-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">🔔 Push notifications blocked</p>
                                <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                                    To enable mobile notifications:
                                </p>
                                <ol className="text-xs text-amber-700 dark:text-amber-400 mt-1 space-y-0.5 list-decimal list-inside leading-relaxed">
                                    <li>Click the <strong>🔒 lock / tune icon</strong> in the address bar</li>
                                    <li>Find <strong>Notifications</strong> → set to <strong>Allow</strong></li>
                                    <li>Reload the page</li>
                                </ol>
                            </div>
                        )}
                        {notifications.length === 0 ? (
                            <div className="p-4 text-center text-sm text-zinc-500 dark:text-zinc-400">
                                No notifications
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification._id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`p-3 rounded-lg text-sm transition-colors cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50 flex items-start gap-2 group ${notification.read ? 'bg-transparent opacity-60' : 'bg-blue-50/70 dark:bg-blue-900/10 border-l-2 border-blue-500'}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-zinc-800 dark:text-zinc-200 leading-snug">
                                                {notification.message}
                                            </p>
                                            <p className="text-xs text-zinc-400 mt-1">
                                                {format(new Date(notification.createdAt), 'MMM d, h:mm a')}
                                            </p>
                                        </div>
                                        <ExternalLink className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500 shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default NotificationDropdown;
